import type { IShortMessage } from './MessageEngineTypes'
import type { IMessage, IMessageStatus } from '@/store/messages/interfaces'

const toShortMessage = (message: IMessage): IShortMessage => ({
  id: message.id,
  type: 'shortMessage',
  timestamp: message.timestamp,
  from: message.from,
  status: message.status
})

export class MessageStore {
  private readonly list: IShortMessage[] = []
  private readonly map = new Map<string, IShortMessage>()
  private dirty = false

  get size() {
    return this.list.length
  }

  getSorted(): readonly IShortMessage[] {
    if (this.dirty) {
      this.list.sort((a, b) => a.timestamp - b.timestamp)
      this.dirty = false
    }
    return this.list
  }

  has(id: string): boolean {
    return this.map.has(id)
  }

  add(message: IMessage): boolean {
    const existing = this.map.get(message.id)
    if (existing) {
      const tsChanged = this.updateTimestamp(message.id, message.timestamp)
      const statusChanged = this.updateStatus(message.id, message.status)
      return tsChanged || statusChanged
    }
    const short = toShortMessage(message)
    this.list.push(short)
    this.map.set(short.id, short)
    this.dirty = true
    return true
  }

  updateTimestamp(id: string, timestamp: number): boolean {
    const existing = this.map.get(id)
    if (!existing || existing.timestamp === timestamp) {
      return false
    }
    existing.timestamp = timestamp
    this.dirty = true
    return true
  }

  updateStatus(id: string, status: IMessageStatus | undefined): boolean {
    const existing = this.map.get(id)
    if (!existing || existing.status === status) {
      return false
    }
    existing.status = status
    return true
  }

  remove(id: string): boolean {
    const idx = this.list.findIndex(m => m.id === id)
    if (idx < 0) {
      return false
    }
    this.list.splice(idx, 1)
    this.map.delete(id)
    return true
  }

  replaceAll(messages: Record<string, IMessage>): void {
    this.list.length = 0
    this.map.clear()
    for (const message of Object.values(messages)) {
      const short = toShortMessage(message)
      this.list.push(short)
      this.map.set(short.id, short)
    }
    this.dirty = true
  }
}
